"use client";

import type { ReactNode } from "react";
import { X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Typography } from "@/components/shared/typography/typography";
import { AppliedFilterChip } from "./AppliedFilterChip";
import {
  buildAppliedModalFilterChips,
  getCheckboxSelection,
} from "./appliedFilterUtils";
import {
  groupTourFilterCheckboxClass,
  groupTourFilterLabelClass,
  groupTourFilterRowClass,
  groupTourFilterSectionTitleClass,
} from "./filterStyles";
import type {
  MarketplaceDynamicFilter,
  MarketplacePriceFilterValue,
  MarketplaceSelectedFilters,
} from "@/types/api/marketplace";

export interface MarketplaceGroupTourFiltersBodyProps {
  modalFilters: MarketplaceDynamicFilter[];
  selectedFilters: MarketplaceSelectedFilters;
  onCheckboxToggle: (type: string, optionId: number) => void;
  onPriceChange: (type: string, field: "min" | "max", raw: string) => void;
  onPriceFieldRemove: (type: string, field: "min" | "max") => void;
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1 md:space-y-0">
      <Typography variant="p" className={groupTourFilterSectionTitleClass}>
        {title}
      </Typography>
      {children}
    </div>
  );
}

/** Group-tours style filter list — gray section headers, label left / checkbox right. */
export function MarketplaceGroupTourFiltersBody({
  modalFilters,
  selectedFilters,
  onCheckboxToggle,
  onPriceChange,
  onPriceFieldRemove,
}: MarketplaceGroupTourFiltersBodyProps) {
  const chips = buildAppliedModalFilterChips(modalFilters, selectedFilters);

  return (
    <div className="space-y-4 pb-4">
      {chips.length > 0 && (
        <div className="app-container space-y-2 px-4 pt-3">
          <Typography variant="p" className="text-sm font-bold text-gray-900">
            Applied filters
          </Typography>
          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <AppliedFilterChip
                key={chip.id}
                label={chip.label}
                onRemove={() => {
                  if (chip.optionId !== undefined) {
                    onCheckboxToggle(chip.filterType, chip.optionId);
                  } else if (chip.priceField) {
                    onPriceFieldRemove(chip.filterType, chip.priceField);
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      {modalFilters.map((filter) => (
        <FilterSection key={filter.type} title={filter.label}>
          {filter.input_type === "checkbox" &&
            filter.options?.map((option) => {
              const isSelected = getCheckboxSelection(
                selectedFilters,
                filter.type
              ).includes(option.id);

              return (
                <div
                  key={option.id}
                  className={groupTourFilterRowClass}
                  onClick={() => onCheckboxToggle(filter.type, option.id)}>
                  <Label
                    className={groupTourFilterLabelClass}
                    htmlFor={`${filter.type}-${option.id}`}>
                    {option.label}
                  </Label>
                  <Checkbox
                    id={`${filter.type}-${option.id}`}
                    checked={isSelected}
                    onCheckedChange={() =>
                      onCheckboxToggle(filter.type, option.id)
                    }
                    className={groupTourFilterCheckboxClass}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              );
            })}

          {filter.input_type === "price_range" && (
            <div className="app-container flex gap-3 px-5 py-3">
              {(["min", "max"] as const).map((field) => {
                const priceValue = (
                  selectedFilters[filter.type] as
                    | MarketplacePriceFilterValue
                    | undefined
                )?.[field];
                const placeholder = field === "min" ? "Min price" : "Max price";

                return (
                  <div key={field} className="relative min-w-0 flex-1">
                    <input
                      type="number"
                      placeholder={placeholder}
                      value={priceValue ?? ""}
                      onChange={(e) =>
                        onPriceChange(filter.type, field, e.target.value)
                      }
                      className="focus:border-brand-500 w-full rounded-lg border border-gray-300 py-2.5 pr-9 pl-3 text-sm outline-none placeholder:text-gray-400"
                    />
                    {priceValue && (
                      <button
                        type="button"
                        onClick={() => onPriceFieldRemove(filter.type, field)}
                        className="absolute top-1/2 right-2 flex -translate-y-1/2 cursor-pointer items-center justify-center rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        aria-label={`Clear ${placeholder}`}>
                        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </FilterSection>
      ))}
    </div>
  );
}
